import { useFlightTelemetry } from '@/lib/api/queries';
import { config } from '@/lib/config';
import { cn } from '@/lib/utils';
import { ArrowLeft } from 'lucide-react';
import { useMemo, useState } from 'react';
import { AltitudeProfile } from './AltitudeProfile';
import { FlightMap } from './FlightMap';
import { FlightParams } from './FlightParams';
import { FlightTrackPanel } from './FlightTrackPanel';
import { useFilteredFlights, useFlightsStore } from './FlightsStore';
import { SyncFlightDialog } from './SyncFlightDialog';
import { STATUS_TONE, formatDuration, formatNum } from './columns';

export function FlightDetail() {
  const { data } = useFilteredFlights();
  const openFlightId = useFlightsStore((s) => s.openFlightId);
  const openFlight = useFlightsStore((s) => s.openFlight);
  const [syncOpen, setSyncOpen] = useState(false);
  // Single source of truth for the scrub cursor — the track table, the
  // altitude profile and the map marker all read it, so it cannot live in
  // any one of them. The mockup instead reached into the DOM by id from
  // three separate functions.
  const [scrubIndex, setScrubIndex] = useState(0);

  const listFlight = useMemo(
    () => (data ?? []).find((f) => f.flight_id === openFlightId) ?? null,
    [data, openFlightId],
  );

  // Telemetry history is its own call, made only while this view is open.
  const telemetry = useFlightTelemetry(listFlight);

  // The list record with its track folded in. `data_point_count` comes from
  // the response's own `count`; the other aggregates stay null because the
  // endpoint does not send them and computing them here would present a
  // number we invented as one the backend measured.
  const flight = useMemo(() => {
    if (!listFlight || !telemetry.data) return listFlight;
    return {
      ...listFlight,
      track: telemetry.data.points,
      data_point_count: telemetry.data.count,
    };
  }, [listFlight, telemetry.data]);

  // Reset the cursor when the open flight changes — a shorter track must not
  // be indexed with a cursor left over from a longer one. This is React's
  // documented "adjust state during render" pattern rather than an effect:
  // it lands before paint, so no frame renders with a stale index.
  const [lastFlightId, setLastFlightId] = useState(openFlightId);
  if (openFlightId !== lastFlightId) {
    setLastFlightId(openFlightId);
    setScrubIndex(0);
  }

  if (!flight) return null;

  const track = flight.track;
  const scrubPoint = track?.[scrubIndex] ?? null;
  const canSync = config.isMock && flight.sync_status !== 'Synced';

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-(--color-border) shrink-0">
        <button
          type="button"
          onClick={() => openFlight(null)}
          className="flex items-center gap-1 text-[0.769em] text-(--color-text2) hover:text-(--color-text)"
        >
          <ArrowLeft size={13} /> Back to Flights
        </button>

        <span className={cn('ml-3 text-[0.769em] font-bold', STATUS_TONE[flight.flight_status])}>
          {flight.flight_status}
        </span>
        {flight.aircraft_status && (
          <span className="text-[0.769em] text-(--color-text2)">{flight.aircraft_status}</span>
        )}
        {flight.duration_s != null && (
          <span
            className="text-[0.769em] text-(--color-text2)"
            title={
              flight.duration_approx
                ? 'Approximate — derived from record timestamps, not a measured flight window'
                : undefined
            }
          >
            {flight.duration_approx ? '~' : ''}
            {formatDuration(flight.duration_s)}
          </span>
        )}

        {config.isMock && (
          <span className="text-[0.692em] text-(--color-text3)">{flight.sync_status}</span>
        )}

        <button
          type="button"
          onClick={() => setSyncOpen(true)}
          disabled={config.isMock && !canSync}
          className="ml-auto h-6 px-2.5 rounded-full bg-(--color-green) text-(--color-on-accent) text-[0.692em] font-bold disabled:opacity-50"
        >
          {config.isMock && flight.sync_status === 'Synced' ? '✓ Synced' : 'Sync Now'}
        </button>
      </div>

      <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">
        <div className="flex-1 min-w-0 overflow-y-auto p-3 space-y-3">
          <div className="text-[0.846em] font-bold">Flight Parameters</div>
          <FlightParams flight={flight} />
          <FlightTrackPanel
            flight={flight}
            scrubIndex={scrubIndex}
            onScrub={setScrubIndex}
            loading={telemetry.isPending && telemetry.fetchStatus !== 'idle'}
            error={telemetry.error}
          />
        </div>

        <div className="flex-1 min-w-0 border-t lg:border-t-0 lg:border-l border-(--color-border) flex flex-col">
          {/* Keyed by flight so the map instance is rebuilt when the open
              flight changes — its init effect is mount-only on purpose, so a
              flight with no geometry could otherwise never gain a map. The
              track length is in the key for the same reason: telemetry
              arrives after mount, and the flown track has to replace the
              planned-waypoint line once it does. Both halves are stable
              across query refetches, which is what keeps the instance from
              being torn down every 60s. */}
          <FlightMap
            key={`${flight.flight_id}:${track?.length ?? 'pending'}`}
            flight={flight}
            scrubIndex={scrubIndex}
          />

          {track != null && track.length > 1 && (
            <div className="border-t border-(--color-border) p-3 space-y-2 shrink-0">
              <AltitudeProfile track={track} scrubIndex={scrubIndex} onScrub={setScrubIndex} />
              <input
                type="range"
                min={0}
                max={track.length - 1}
                step={1}
                value={scrubIndex}
                onChange={(e) => setScrubIndex(Number(e.target.value))}
                className="w-full accent-(--color-green) cursor-pointer"
                aria-label="Scrub flight track"
              />
              {scrubPoint && (
                <div className="text-[0.692em] font-mono text-(--color-text2)">
                  {scrubPoint.t ?? '—'} · {formatNum(scrubPoint.lat, 5)},{' '}
                  {formatNum(scrubPoint.lon, 5)} · WGS84 {formatNum(scrubPoint.alt_wgs84, 1, 'm')} /
                  ASL {formatNum(scrubPoint.alt_asl, 1, 'm')} / AGL{' '}
                  {formatNum(scrubPoint.alt_agl, 1, 'm')} ·{' '}
                  {formatNum(scrubPoint.speed_ms, 1, ' m/s')} · {scrubPoint.flight_mode ?? '—'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <SyncFlightDialog flight={flight} open={syncOpen} onOpenChange={setSyncOpen} />
    </div>
  );
}
